import Cookies from "js-cookie"
import queryString from "query-string"
import { replace } from "redux-first-history"
import { all, call, put, select } from "redux-saga/effects"

import api from "apis"
import { addAlert } from "app-store-v2/app/actions"
import { isStoneOrAccentPref } from "areas/product/jewlr"
import { submit_ga_tracker } from "helpers/analytics-helpers"
import { perfMark, isTestMode } from "helpers/application"
import { getProductPrefs } from "helpers/user-prefs"

import {
  updateImages,
  addCoupon,
  addWizardData,
  clearChoices,
  deactivatePrefStore,
  forcePanelIndex,
  loadDefaults,
  scrollPanelIndex,
  setExtraWizardParams,
  setSignatureBasket,
  setWizardHeader,
  setWizardLoading,
  setWizardPrefOrder,
  updateChoice,
  updateShipping,
  replaceWizardData,
  productNotFound,
  productFound,
  resetPaneFilters,
  fetchStylecode,
} from "../actions"

export default function* loadWizard(action) {
  perfMark("product:load_wizard:start")
  const state = yield select()
  const styleCode = action.data.style_code
  const query = queryString.parse(state.router.location.search)
  const ssr = state.router.action == "POP"
  const is_flip_sku = Object.keys(state.wizard.data).find((k) =>
    k.includes("face")
  )

  // This invokes the "Update Cart" by setting the line_item_id in the store
  if (query.line_item_id) {
    yield put(setExtraWizardParams({ lid: query.line_item_id }))
  }
  // This ensures that the user pref selected box is not set to the user prefs when edit item is invoked
  // (passed from the Edit Line Item URL as a parameter
  if (query.nobox) {
    yield put(setExtraWizardParams({ nobox: true }))
  } else if (state.wizardUI.nobox) {
    yield put(setExtraWizardParams({ nobox: false }))
  }
  // This opens accordion panel and scrolls to it, if panel is present in query
  if (query.panel) {
    yield put(forcePanelIndex(query.panel))
    yield put(scrollPanelIndex(query.panel))
  }
  // This sets today so we can test inventory for subsequent calls within update-price-shipping-time
  if (query.today) {
    yield put(setExtraWizardParams({ today: query.today }))
  }
  if (query.time) {
    yield put(setExtraWizardParams({ time: query.time }))
  }
  // This gets the product-specific user preferences for any particular style code
  let localPrefs = getProductPrefs(styleCode)
  let userPrefs = Cookies.getJSON("userPreferences") || {}
  try {
    const userPrefValues = Object.values(userPrefs).join("")
    const test = encodeURIComponent(userPrefValues) // eslint-disable-line no-unused-vars
  } catch (e) {
    // userPrefs have malformed characters, so we reset and start over with them
    Cookies.remove("userPreferences")
    userPrefs = {}
  }

  // Special flag, checks if engravings have been provided through query string or by user, and skips
  // using the default engravings in this particular case.
  let skipDefaultEngravings =
    Object.keys(localPrefs).find((k) => k.match(/^e[0-9]+$/)) ||
    Object.keys(userPrefs).find((k) => k.match(/^e[0-9]+$/)) ||
    Object.keys(query).find((k) => k.match(/^e[0-9]+$/))

  // Special flag, checks if images have been provided through query string or by user, and skips
  // using the default images in this particular case.
  let skipDefaultImages =
    Object.keys(localPrefs).find((k) => k.match(/^img[0-9]+$/)) ||
    Object.keys(userPrefs).find((k) => k.match(/^img[0-9]+$/)) ||
    Object.keys(query).find((k) => k.match(/^img[0-9]+$/))

  // This fetches the remaining product defaults. We pass the values from qs so valid values for the product are
  // returned
  // The actual fetch occurs after the prefStore is loaded, unless we are in switchMode
  if (state.wizard.adminMode) {
    query.include_unsearchable = true
    query.from_admin = true
  } else {
    Object.keys(localPrefs).forEach((key) => {
      // qs trumps localPrefs
      if (typeof query[key] === "undefined") {
        query[key] = localPrefs[key]
      }
    })
  }

  // Load in the Product Wizard data before calling product_defaults
  // The data inside of the products_controller#react_product method is loaded in using the following
  // code
  let data_loads = []
  let image_data_loads = []
  let preload = {}
  let result = {}
  let signatureBasket = false
  if (action.data.switchMode) {
    try {
      // params.append("switchMode", "1")
      if (action.data.olid && query.olid) {
        query.olid = action.data.olid
      }
      const url = `/api/v10/products/${styleCode}/product_defaults?${queryString.stringify(query)}`
      result = yield call(api.get, url)
      if (result?.data?.redirect) {
        yield put(replace(result.data.redirect))
        let redirect_params = new URLSearchParams()
        // redirect_params.append("switchMode", "1")
        Object.keys(result.data.override_params).forEach((key) => {
          redirect_params.append(key, result.data.override_params[key])
        })
        const redirect_url = `/api/v10/products/${
          result.data.style_code
        }/product_defaults?${redirect_params.toString()}`
        result = yield call(api.get, redirect_url)
      }

      // SPA navigation, update the PDP unique event id
      if (result?.data?.unique_event_id) {
        window.cache_free_ssr_data.unique_event_ids.pdp =
          result.data.unique_event_id
      }

      yield put(updateImages(result?.data?.product_image))

      preload = {
        pds: result.data.wizard.pds,
        product_title: result.data.wizard.description.title,
        react: result.data.wizard,
      }
      yield put(productFound())
      yield put(setWizardHeader(result.data.wizard.header))
    } catch (error) {
      console.log(error)
      return yield put(productNotFound())
    }
  } else {
    skipDefaultEngravings =
      Object.keys(userPrefs).find((k) => k.match(/^e[0-9]+$/)) ||
      Object.keys(query).find((k) => k.match(/^e[0-9]+$/))

    preload = {
      pds: state.choices,
      product_title: state.wizard.description.title,
      react: state.wizard,
    }
  }

  if (!isTestMode) {
    dataLayer.push({
      event: "google-remarketing",
      googleRemarketing: {
        ecomm_pagetype: "product",
        ecomm_prodid: `${styleCode}-base`,
        ecomm_totalvalue: preload.react.base_ss_price,
      },
    })
    submit_ga_tracker(`/products/${styleCode}`)
  }

  // global_store_pref_flag = false
  if (!ssr) {
    yield put(deactivatePrefStore())
  }

  if (preload.react) {
    if (preload.react.masterStyleCode) {
      yield put(replace(preload.react.base_product_url))
      yield put(fetchStylecode(preload.react.masterStyleCode, true))
      return
    }
    if (!ssr) {
      yield put(replaceWizardData(preload.react.data))
      // Need to clear existing data from wizard
      yield put(resetPaneFilters())
    }

    Object.keys(preload.react.data).forEach((key) => {
      let data = preload.react.data[key]
      if (key !== "diamond") {
        !ssr && data_loads.push(put(addWizardData(data)))
        if (data.engravings) {
          data.engravings.forEach((engraving) => {
            if (state.wizard.adminMode) {
              engraving.any_case = true
              engraving.max_chars = engraving.admin_max_chars
            }
            if (
              state.choices.sc &&
              engraving.engraving_num > state.choices.sc.value
            ) {
              engraving.disabled = true
            }

            engraving.is_default = !skipDefaultEngravings
            data_loads.push(put(addWizardData(engraving)))
          })
        } else if (/img[0-9]+/.test(data.pref)) {
          data.is_default = !skipDefaultImages
          image_data_loads.push(
            put(
              updateChoice(data.pref, {
                is_default: !skipDefaultImages,
              })
            )
          )
        }
        // TEMP: Fix Flip SKUs due to incorrect preloaded data in Redux for stone options
        if ((!ssr || is_flip_sku) && data.stones) {
          data.stones.forEach((stone) => {
            data_loads.push(put(addWizardData(stone)))
          })
        }
        if (data.default_sku) {
          data_loads.push(put(addWizardData(data.default_sku)))
        }
      }

      if (data.is_signature || data.is_default_signature) {
        signatureBasket = true
        data_loads.push(put(setSignatureBasket(true)))
      }
    })

    if (preload.react.default_sku) {
      yield put(addWizardData(preload.react.default_sku))
      yield put(updateChoice("sku", { value: "NA" }))
    }

    if (preload.react.prefOrder) {
      const hasTwoOrMoreStoneOrAccentOptions =
        preload.react.prefOrder.reduce(
          (count, pref) => count + (isStoneOrAccentPref(pref) ? 1 : 0),
          0
        ) >= 2
      const shouldUseNewWizardExp =
        state.experiment.guidedStoneSelector?.variant_id === 1 &&
        hasTwoOrMoreStoneOrAccentOptions
      yield put(
        setWizardPrefOrder(preload.react.prefOrder, shouldUseNewWizardExp)
      )
    }

    if (preload.react.coupon) {
      yield put(addCoupon(preload.react.coupon))
    }

    const editContext =
      state.wizard.adminMode || state.wizard.olid || state.wizard.formUrl
        ? {
            formUrl: state.wizard.formUrl,
            olid: state.wizard.olid,
            orderParams: state.wizard.orderParams,
          }
        : {}

    yield put(
      setExtraWizardParams(
        {
          ...preload.react,
          ...editContext,
        },
        true
      )
    )
  }
  yield all(data_loads)

  // Reset the choices when switching style codes to prevent issues with disabled choices
  // or other issues with missing engraving restrictions i.e. JWLN0301 (min_chars)
  if (!ssr) {
    yield put(clearChoices())
  }
  yield put(loadDefaults(preload.pds))

  if (image_data_loads.length) yield all(image_data_loads)

  if (!signatureBasket && preload.react.data.diamond) {
    yield put(updateChoice("e1", { disabled: true }))
    yield put(updateChoice("e2", { disabled: true }))
  }

  if (
    preload.react.notification &&
    Object.keys(preload.react.notification).length > 0
  ) {
    const { notification } = preload.react
    window.setTimeout(function () {
      appStoreV2.dispatch(addAlert(notification))
      submit_ga_event_click("Notification", "show", name, null, null, true)
    }, notification.delay * 1000)
  }

  yield put(setWizardLoading(""))

  //Moved shipping info to react
  yield put(updateShipping(preload.react.shipping))

  // For non-NA regions, make a call to set delivery cookie immediately if the cookie doesn't yet exist
  // try {
  //   if (!is_na && Cookies.get(sl.shiptime_cookie_name) === undefined) {
  //     let element = document.getElementById("js_est_delivery_form")
  //     if (typeof element != "undefined" && element != null) {
  //       let formSubmitButton = document.getElementById("js_est_delivery_submit")
  //       formSubmitButton.click()
  //     }
  //   }
  // } catch (e) {
  //   console.error(e)
  // }

  // Sync browser URL query string with backend-validated product params.
  if (!ssr && preload.react.validated_query_string !== undefined) {
    const currentState = yield select()
    const currentLocation = currentState.router.location
    const currentQuery = queryString.parse(currentLocation.search)
    const backendQuery = queryString.parse(preload.react.validated_query_string)
    const backendKeys = preload.react.validated_query_keys || []
    let changed = false

    backendKeys.forEach((key) => {
      const value = backendQuery[key]

      if (!value) {
        if (typeof currentQuery[key] !== "undefined") {
          delete currentQuery[key]
          changed = true
        }
      } else if (currentQuery[key] !== value) {
        currentQuery[key] = value
        changed = true
      }
    })

    if (changed) {
      yield put(
        replace({
          ...currentLocation,
          search: queryString.stringify(currentQuery),
        })
      )
    }
  }

  perfMark("product:load_wizard:finish")

  if (query.formUrl) {
    yield put(setExtraWizardParams({ formUrl: query.formUrl }))
    yield put(setExtraWizardParams({ orderParams: query.orderParams }))
  }
}
